// Vet-facing release notes: fetch the manifest the build ships next to
// VERSION.txt, and decide whether this browser still owes the vet a
// "What's new" for the latest entry.
//
// Source of truth is release-notes/*.md, compiled by scripts/release-notes/
// build.mjs into dist/release-notes.json (see release-notes/README.md).

const RUNNING_SHA = typeof __BUILD_GIT_SHA__ !== 'undefined' ? __BUILD_GIT_SHA__ : 'unknown'

export const RELEASE_NOTES_URL = '/vet-portal/release-notes.json'
export const LAST_SEEN_KEY = 'vetpo.releaseNotes.lastSeen'
/** A note older than this is history, not news: never pop it up unasked. */
export const RECENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
const FETCH_TIMEOUT_MS = 5000

export interface ReleaseNoteEntry {
  /** Filename stem, immutable once shipped — this is what "seen" remembers. */
  id: string
  /** YYYY-MM-DD */
  date: string
  title: string
  /** Markdown */
  body: string
}

export interface ReleaseNotesManifest {
  schema: number
  version: string | null
  generated: string
  latest: string | null
  entries: ReleaseNoteEntry[]
}

const isEntry = (x: unknown): x is ReleaseNoteEntry => {
  if (!x || typeof x !== 'object') return false
  const e = x as Record<string, unknown>
  return (
    typeof e.id === 'string' &&
    e.id.length > 0 &&
    typeof e.date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(e.date) &&
    typeof e.title === 'string' &&
    typeof e.body === 'string'
  )
}

/**
 * Validate an untrusted manifest. Returns null on any shape problem — the
 * notice must fail toward silence, never toward rendering garbage over a
 * vet's chart. Entries are re-sorted newest-first so the client never trusts
 * file order.
 */
export function parseManifest(raw: unknown): ReleaseNotesManifest | null {
  if (!raw || typeof raw !== 'object') return null
  const m = raw as Record<string, unknown>
  if (m.schema !== 1 || !Array.isArray(m.entries)) return null
  if (!m.entries.every(isEntry)) return null
  const entries = [...(m.entries as ReleaseNoteEntry[])].sort((a, b) =>
    a.date === b.date ? (a.id < b.id ? 1 : -1) : a.date < b.date ? 1 : -1,
  )
  return {
    schema: 1,
    version: typeof m.version === 'string' ? m.version : null,
    generated: typeof m.generated === 'string' ? m.generated : '',
    latest: entries[0]?.id ?? null,
    entries,
  }
}

/**
 * The one rule behind the notice, pure for testability:
 * show the newest entry when this browser has not acknowledged it AND it is
 * recent. A never-acknowledged browser (new machine, cleared storage) sees the
 * latest note only if it is still news; a months-old note stays in Help.
 */
export function pickUnseenEntry(
  entries: ReleaseNoteEntry[],
  lastSeenId: string | null,
  nowMs: number,
): ReleaseNoteEntry | null {
  const latest = entries[0]
  if (!latest) return null
  if (latest.id === lastSeenId) return null
  const published = Date.parse(`${latest.date}T00:00:00Z`)
  if (!Number.isFinite(published)) return null
  if (nowMs - published > RECENT_WINDOW_MS) return null
  return latest
}

export function readLastSeen(): string | null {
  try {
    return window.localStorage.getItem(LAST_SEEN_KEY)
  } catch {
    return null
  }
}

export function writeLastSeen(id: string): void {
  try {
    window.localStorage.setItem(LAST_SEEN_KEY, id)
  } catch {
    // Private mode / blocked storage: the notice will simply show again next
    // load. Acceptable — better than throwing inside a render effect.
  }
}

export async function fetchReleaseNotes(): Promise<ReleaseNotesManifest | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    // Keyed on the running bundle's sha: the manifest is part of the same
    // artifact, so one cache entry per build is exactly right, and a stale
    // proxy copy cannot outlive the bundle that references it.
    const res = await fetch(`${RELEASE_NOTES_URL}?v=${encodeURIComponent(RUNNING_SHA)}`, {
      signal: controller.signal,
      // Same reason as useVersionCheck: the /vet-portal/ root is cookie-switched
      // (ENV=beta), and a cookieless request would read PROD's notes on beta.
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    // nginx answers the SPA shell (200 + HTML) for any unknown path, which is
    // exactly what an old build with no manifest looks like. Check the type
    // before parsing so that case is "no notes", not a JSON error in the console.
    const type = res.headers.get('content-type') ?? ''
    if (!type.includes('json')) return null
    return parseManifest(await res.json())
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
